/** Plantillas de efectos para aplicar a clips de un click. */

import type { Effect } from "../api/projects";

export interface EffectPreset {
  id: string;
  label: string;
  category: string;
  icon: string;
  desc: string;
  /** Construye un nuevo stack de efectos para el clip. */
  build: (newId: () => string) => Effect[];
}

export const EFFECT_PRESETS: EffectPreset[] = [
  {
    id: "none",
    label: "Sin efectos",
    category: "Limpio",
    icon: "○",
    desc: "Quita todos los efectos del clip",
    build: () => [],
  },

  // ===== Looks cinematográficos ==========================================
  {
    id: "cinematic",
    label: "Cinematográfico",
    category: "Cinematográfico",
    icon: "🎬",
    desc: "Teal & orange + contraste alto",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.15, saturation: 0.95, temperature: 0.1, enabled: true } as any,
      { id: uid(), type: "lut", file: "cinematic.cube", intensity: 0.85, enabled: true } as any,
    ],
  },
  {
    id: "blockbuster",
    label: "Blockbuster",
    category: "Cinematográfico",
    icon: "🍿",
    desc: "Tono Hollywood: orange & teal saturados",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.25, saturation: 1.1, temperature: 0.15, exposure: 0.1, enabled: true } as any,
      { id: uid(), type: "lut", file: "cinematic.cube", intensity: 1.0, enabled: true } as any,
    ],
  },
  {
    id: "moody",
    label: "Moody",
    category: "Cinematográfico",
    icon: "🌫",
    desc: "Apagado, dramático, frío",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: -0.3, contrast: 1.2, saturation: 0.7, temperature: -0.2, enabled: true } as any,
    ],
  },

  // ===== Cálidos / Fríos =================================================
  {
    id: "warm_sun",
    label: "Sol cálido",
    category: "Calidez",
    icon: "☀",
    desc: "Atardecer dorado",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: 0.15, temperature: 0.35, saturation: 1.05, enabled: true } as any,
      { id: uid(), type: "lut", file: "warm.cube", intensity: 0.7, enabled: true } as any,
    ],
  },
  {
    id: "cool_blue",
    label: "Azul frío",
    category: "Calidez",
    icon: "❄",
    desc: "Mañana azulada / nórdico",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.05, temperature: -0.35, saturation: 0.9, enabled: true } as any,
      { id: uid(), type: "lut", file: "cold.cube", intensity: 0.6, enabled: true } as any,
    ],
  },
  {
    id: "golden_hour",
    label: "Hora dorada",
    category: "Calidez",
    icon: "🌅",
    desc: "Luz suave y dorada",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: 0.25, temperature: 0.4, contrast: 0.95, saturation: 1.1, enabled: true } as any,
      { id: uid(), type: "lut", file: "warm.cube", intensity: 0.9, enabled: true } as any,
    ],
  },

  // ===== Vintage / Retro =================================================
  {
    id: "vintage",
    label: "Vintage",
    category: "Vintage",
    icon: "📷",
    desc: "Look polaroid: desaturado, cálido, lavado",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 0.9, saturation: 0.8, temperature: 0.2, enabled: true } as any,
      { id: uid(), type: "lut", file: "vintage.cube", intensity: 1.0, enabled: true } as any,
    ],
  },
  {
    id: "film70",
    label: "Film años 70",
    category: "Vintage",
    icon: "🎞",
    desc: "Tonalidad de película antigua",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: 0.1, contrast: 1.1, saturation: 0.85, temperature: 0.25, enabled: true } as any,
      { id: uid(), type: "lut", file: "vintage.cube", intensity: 0.85, enabled: true } as any,
    ],
  },

  // ===== Saturación / Vivo ===============================================
  {
    id: "punchy",
    label: "Vivo (saturado)",
    category: "Saturación",
    icon: "💥",
    desc: "Colores impactantes para redes",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.2, saturation: 1.4, enabled: true } as any,
      { id: uid(), type: "lut", file: "punchy.cube", intensity: 1.0, enabled: true } as any,
    ],
  },
  {
    id: "instagram",
    label: "Instagram",
    category: "Saturación",
    icon: "📱",
    desc: "Brillante, saturado, contraste medio",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: 0.1, contrast: 1.1, saturation: 1.25, enabled: true } as any,
    ],
  },

  // ===== Blanco y negro ==================================================
  {
    id: "bw_classic",
    label: "Blanco y negro",
    category: "Blanco y negro",
    icon: "◐",
    desc: "Conversión limpia",
    build: (uid) => [
      { id: uid(), type: "lut", file: "bw.cube", intensity: 1.0, enabled: true } as any,
    ],
  },
  {
    id: "bw_contrast",
    label: "B&N alto contraste",
    category: "Blanco y negro",
    icon: "◑",
    desc: "Estilo periodístico, blancos y negros profundos",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.45, enabled: true } as any,
      { id: uid(), type: "lut", file: "bw.cube", intensity: 1.0, enabled: true } as any,
    ],
  },
  {
    id: "bw_soft",
    label: "B&N suave",
    category: "Blanco y negro",
    icon: "◒",
    desc: "Look documental, contraste bajo",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 0.85, exposure: 0.1, enabled: true } as any,
      { id: uid(), type: "lut", file: "bw.cube", intensity: 0.95, enabled: true } as any,
    ],
  },

  // ===== Ajustes rápidos =================================================
  {
    id: "brighten",
    label: "Brillante",
    category: "Ajustes rápidos",
    icon: "☀",
    desc: "Sube exposición y contraste",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: 0.8, contrast: 1.05, enabled: true } as any,
    ],
  },
  {
    id: "darken",
    label: "Oscuro",
    category: "Ajustes rápidos",
    icon: "🌙",
    desc: "Baja exposición",
    build: (uid) => [
      { id: uid(), type: "adjust", exposure: -0.8, contrast: 1.1, enabled: true } as any,
    ],
  },
  {
    id: "high_contrast",
    label: "Alto contraste",
    category: "Ajustes rápidos",
    icon: "◐",
    desc: "Sólo aumenta el contraste",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 1.4, enabled: true } as any,
    ],
  },
  {
    id: "soft",
    label: "Suave",
    category: "Ajustes rápidos",
    icon: "○",
    desc: "Reduce contraste, look mate",
    build: (uid) => [
      { id: uid(), type: "adjust", contrast: 0.8, saturation: 0.92, enabled: true } as any,
    ],
  },
  {
    id: "desaturate",
    label: "Desaturado",
    category: "Ajustes rápidos",
    icon: "◌",
    desc: "Colores apagados",
    build: (uid) => [
      { id: uid(), type: "adjust", saturation: 0.5, enabled: true } as any,
    ],
  },
];

export const EFFECT_DRAG_MIME = "application/x-videotool-effect-preset";
